import type React from 'react';
import { cn } from '../../utils/cn.js';
import { Button } from '../tailgrids/core/button.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '../tailgrids/core/dropdown.js';
import { MenuDotsIcon } from '../shell/shell-icons.js';

export interface ActionMenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onAction: () => void;
  /** `danger` colours a destructive item (delete, revoke). */
  tone?: 'danger';
  isDisabled?: boolean;
}

interface ActionMenuProps {
  items: readonly ActionMenuItem[];
  /** Names the trigger for screen readers, e.g. "Actions for Quarterly review". */
  'aria-label': string;
  className?: string;
}

/**
 * The ⋯ menu on a table row or a card corner. Template example: NextAdmin `(dashboard)/(home)/_component/top-products`
 * (`Button iconOnly size="xs" variant="ghost"` with `MenuDotsIcon`) and demo `/ui-elements/dropdowns`, the menu being
 * the vendored `DropdownMenu`.
 */
export function ActionMenu({ items, className, ...aria }: ActionMenuProps) {
  return (
    <DropdownMenu>
      <Button
        iconOnly
        size="xs"
        variant="ghost"
        aria-label={aria['aria-label']}
        className={cn('text-icon-secondary', className)}
      >
        <MenuDotsIcon />
      </Button>
      <DropdownMenuContent placement="bottom end" className="p-1.5">
        {items.map((item) => (
          <DropdownMenuItem
            key={item.id}
            id={item.id}
            textValue={item.label}
            isDisabled={item.isDisabled}
            onAction={item.onAction}
            className={cn(
              'cursor-pointer px-3 py-2',
              item.tone === 'danger' && 'text-error-500 focus:text-error-500 data-disabled:text-text-tertiary',
              item.isDisabled && 'cursor-not-allowed opacity-60',
            )}
          >
            {item.icon && <span className="shrink-0 [&>svg]:size-4">{item.icon}</span>}
            <span className="font-medium">{item.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
